import { useEffect, useRef } from "react";
import { Map as MapLibreMap, Marker, NavigationControl } from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";

const STYLE = {
  version: 8,
  sources: {
    basemap: {
      type: "raster",
      tiles: [
        "https://a.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}@2x.png",
        "https://b.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}@2x.png",
        "https://c.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}@2x.png",
      ],
      tileSize: 256,
      attribution: "© OpenStreetMap, © CARTO",
    },
  },
  layers: [
    { id: "bg", type: "background", paint: { "background-color": "#0B1B33" } },
    { id: "basemap", type: "raster", source: "basemap" },
  ],
};

const pinEl = (plate) => {
  const el = document.createElement("div");
  el.setAttribute("data-testid", "admin-drag-marker");
  el.style.cssText = "cursor:grab;display:flex;flex-direction:column;align-items:center";
  el.innerHTML = `<div style="width:16px;height:16px;border-radius:50%;background:#C8A24A;box-shadow:0 0 0 4px rgba(200,162,74,.25)"></div>
    <div style="margin-top:3px;padding:1px 5px;background:#0B1B33;border:1px solid #C8A24A;color:#F3E7C9;font-size:9px;font-weight:700;letter-spacing:.08em;white-space:nowrap">${plate}</div>`;
  return el;
};

/* Admin-only draggable truck marker: drop the pin, we snap it to the road and save the override. */
export const AdminDragMap = ({ position, plate, route = [], onDrop }) => {
  const holder = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const dropRef = useRef(onDrop);
  dropRef.current = onDrop;

  useEffect(() => {
    if (mapRef.current) return;
    const map = new MapLibreMap({
      container: holder.current,
      style: STYLE,
      center: [position?.lng ?? 93.7, position?.lat ?? 25.9],
      zoom: position ? 12 : 5.5,
      attributionControl: false,
    });
    map.addControl(new NavigationControl({}), "top-right");
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !position) return;
    if (!markerRef.current) {
      markerRef.current = new Marker({ element: pinEl(plate || "TRUCK"), draggable: true })
        .setLngLat([position.lng, position.lat])
        .addTo(map);
      markerRef.current.on("dragend", () => {
        const { lat, lng } = markerRef.current.getLngLat();
        dropRef.current?.({ lat, lng });
      });
    } else {
      markerRef.current.setLngLat([position.lng, position.lat]);
      markerRef.current.getElement().innerHTML = pinEl(plate || "TRUCK").innerHTML;
    }
    map.easeTo({ center: [position.lng, position.lat], zoom: Math.max(map.getZoom(), 11), duration: 600 });
  }, [position?.lat, position?.lng, plate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const data = {
      type: "Feature",
      properties: {},
      geometry: { type: "LineString", coordinates: route.map(([lat, lng]) => [lng, lat]) },
    };
    const draw = () => {
      if (map.getSource("admin-route")) {
        map.getSource("admin-route").setData(data);
        return;
      }
      map.addSource("admin-route", { type: "geojson", data });
      map.addLayer({
        id: "admin-route-line",
        type: "line",
        source: "admin-route",
        paint: { "line-color": "#C8A24A", "line-width": 3, "line-opacity": 0.7 },
      });
    };
    if (map.isStyleLoaded()) draw();
    else map.once("load", draw);
  }, [route]);

  return <div ref={holder} data-testid="admin-drag-map" className="h-[320px] w-full" />;
};
